import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Package, Banknote, Handshake, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import api from '@/lib/api';
import { toast } from 'sonner';

const getMediaUrl = (url) => (!url ? '' : url.startsWith('http') ? url : `${url}`);

export default function DealBuilderPage() {
  const { itemId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const editingOfferId = searchParams.get('offerId');
  const preMsg = searchParams.get('preMsg');
  const [targetItem, setTargetItem] = useState(null);
  const [myItems, setMyItems] = useState([]);
  const [selectedItemId, setSelectedItemId] = useState(searchParams.get('itemId') || null);
  const [cashAmount, setCashAmount] = useState(searchParams.get('cash') || '');
  const [message, setMessage] = useState(preMsg || '');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    Promise.all([api.get(`/items/${itemId}`), api.get('/items/my')]).then(([itemRes, myRes]) => {
      setTargetItem(itemRes.data);
      setMyItems(myRes.data.filter(i => i.status === 'available'));
      setLoading(false);
    }).catch(() => { toast.error('Error al cargar'); setLoading(false); });
  }, [itemId]);

  const selectedItem = myItems.find(i => i.item_id === selectedItemId);
  const totalOffer = (selectedItem?.price || 0) + (parseFloat(cashAmount) || 0);

  const handleSubmit = async () => {
    if (!cashAmount && !selectedItemId) { toast.error('Ofrece al menos un articulo o efectivo'); return; }
    setSubmitting(true);
    try {
      if (editingOfferId) {
        await api.put(`/offers/${editingOfferId}`, {
          offered_item_id: selectedItemId || null,
          cash_amount: parseFloat(cashAmount) || 0,
        });
        toast.success('Oferta mejorada!');
      } else {
        await api.post('/offers', {
          target_item_id: itemId,
          offered_item_id: selectedItemId || null,
          cash_amount: parseFloat(cashAmount) || 0,
          message,
        });
        toast.success('Oferta enviada!');
      }
      navigate('/deals');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally { setSubmitting(false); }
  };

  if (loading) return <div className="min-h-screen bg-background flex items-center justify-center"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;
  if (!targetItem) return <div className="min-h-screen bg-background flex flex-col items-center justify-center p-8"><p className="text-muted-foreground font-body">Articulo no encontrado</p><Button onClick={() => navigate('/')} className="mt-4 rounded-sm font-body">VOLVER</Button></div>;

  return (
    <div data-testid="deal-builder-page" className="min-h-screen bg-background page-with-nav">
      <div className="sticky top-0 z-30 bg-background/95 backdrop-blur-sm border-b-2 border-border px-4 py-3 flex items-center gap-3">
        <button data-testid="deal-back-btn" onClick={() => navigate(-1)}><ArrowLeft className="w-6 h-6 text-foreground" /></button>
        <h1 className="font-heading text-xl text-primary uppercase">{editingOfferId ? 'MEJORAR OFERTA' : 'NEGOCIAR'}</h1>
      </div>

      <div className="max-w-md mx-auto px-4 py-4 space-y-4">
        <div className="bg-card border-2 border-border rounded-sm p-4">
          <p className="text-muted-foreground text-xs font-body uppercase tracking-wider mb-3">QUIERO ESTO</p>
          <div className="flex gap-4">
            <div className="w-24 h-24 bg-zinc-900 rounded-sm overflow-hidden shrink-0">
              {(targetItem.images?.[0] || targetItem.image_url || targetItem.video_url) && (
                <img src={getMediaUrl(targetItem.images?.[0] || targetItem.image_url || targetItem.video_url)} className="object-cover w-full h-full" alt={targetItem.title} />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-body font-bold text-foreground text-base truncate">{targetItem.title}</h3>
              <p className="font-heading text-2xl text-primary">${targetItem.price?.toLocaleString()} MXN</p>
              <p className="text-muted-foreground text-xs font-body">@{targetItem.seller_name?.toLowerCase().replace(/\s/g,'_')}</p>
            </div>
          </div>
        </div>

        <div className="border-b-4 border-dashed border-zinc-700" />

        <div className="space-y-4">
          <p className="text-primary text-xs font-body uppercase tracking-wider font-bold">MI OFERTA</p>

          <div className="space-y-2">
            <label className="font-body text-sm text-foreground flex items-center gap-2"><Package className="w-4 h-4" /> Articulo de intercambio (opcional)</label>
            {myItems.length === 0 ? (
              <div className="bg-card border-2 border-dashed border-border rounded-sm p-4 text-center">
                <p className="text-muted-foreground text-sm font-body">Tu locker esta vacio</p>
                <Button onClick={() => navigate('/upload')} variant="outline" size="sm" className="mt-2 rounded-sm font-body text-xs">SUBIR ARTICULO</Button>
              </div>
            ) : (
              <div className="flex gap-3 overflow-x-auto scrollbar-hide pb-2">
                {myItems.map(item => (
                  <button key={item.item_id} data-testid={`deal-select-${item.item_id}`}
                    onClick={() => setSelectedItemId(selectedItemId===item.item_id ? null : item.item_id)}
                    className={`shrink-0 w-28 bg-card border-2 rounded-sm overflow-hidden transition-colors ${selectedItemId===item.item_id ? 'border-primary' : 'border-border'}`}>
                    <div className="aspect-square bg-zinc-900 relative">
                      {(item.images?.[0]||item.image_url||item.video_url) ? (
                        <img src={getMediaUrl(item.images?.[0]||item.image_url||item.video_url)} className="object-cover w-full h-full" alt={item.title} />
                      ) : <div className="w-full h-full flex items-center justify-center"><Package className="w-6 h-6 text-muted-foreground" /></div>}
                      {selectedItemId===item.item_id && <div className="absolute inset-0 bg-primary/20 flex items-center justify-center"><Check className="w-8 h-8 text-primary" /></div>}
                    </div>
                    <div className="p-2">
                      <p className="text-[11px] font-body text-foreground truncate">{item.title}</p>
                      <p className="text-xs font-heading text-primary">${item.price?.toLocaleString()}</p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-2">
            <label className="font-body text-sm text-foreground flex items-center gap-2"><Banknote className="w-4 h-4" /> Ribete en efectivo (MXN)</label>
            <Input data-testid="deal-cash-input" type="number" value={cashAmount} onChange={e => setCashAmount(e.target.value)} placeholder="0" className="rounded-sm border-2 bg-card font-body h-12 text-lg" />
          </div>

          {!editingOfferId && (
            <div className="space-y-2">
              <label className="font-body text-sm text-foreground">Mensaje (opcional)</label>
              <textarea data-testid="deal-message-input" value={message} onChange={e => setMessage(e.target.value)} placeholder="Algun comentario?" rows={2}
                className="w-full rounded-sm border-2 border-border bg-card font-body text-foreground p-3 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-primary" />
            </div>
          )}
        </div>

        <div className="bg-card border-2 border-primary/30 rounded-sm p-4 space-y-2">
          <p className="text-xs font-body text-muted-foreground uppercase tracking-wider">RESUMEN DE OFERTA</p>
          {selectedItem && <div className="flex justify-between font-body text-sm"><span className="text-foreground truncate mr-2">{selectedItem.title}</span><span className="text-primary shrink-0">${selectedItem.price?.toLocaleString()}</span></div>}
          {parseFloat(cashAmount) > 0 && <div className="flex justify-between font-body text-sm"><span className="text-foreground">Efectivo (Ribete)</span><span className="text-primary">+ ${parseFloat(cashAmount).toLocaleString()}</span></div>}
          <Separator />
          <div className="flex justify-between font-heading text-xl"><span className="text-foreground">TOTAL</span><span className="text-primary">${totalOffer.toLocaleString()} MXN</span></div>
        </div>

        <Button data-testid="deal-submit-btn" onClick={handleSubmit} disabled={submitting||(!cashAmount && !selectedItemId)}
          className="w-full h-14 rounded-sm font-body font-bold uppercase text-base bg-primary text-primary-foreground hover:bg-primary/90">
          <Handshake className="w-5 h-5 mr-2" /> {submitting ? 'ENVIANDO...' : editingOfferId ? 'MEJORAR OFERTA' : 'ENVIAR OFERTA'}
        </Button>
      </div>
    </div>
  );
}
